import { ISimulationScore } from '@/types';
import { getSkillLevel } from '@/domains/scoring/skill-level-policy';
import { SCORE_LABELS, SKILL_LEVEL_BY_CODE, SKILL_LEVEL_DEFINITIONS } from '@/constants';

// ================================
// getScoreCardDescription
// ================================

/**
 * Generates a human-readable motivational description for a simulation scorecard
 * based on the user's computed skill level.
 *
 * ## Why this exists
 * The backend does not currently return a description tailored to the user's
 * performance tier. This utility bridges that gap by mapping the numeric score
 * to one of four pre-defined coaching messages stored in `SCORE_LABELS`.
 *
 * @TODO Remove this function once the backend provides scorecard descriptions
 *       directly on the `ISimulationScore` response object.
 *
 * ## Stable level ID → label mapping
 * | Level ID | Label key                            |
 * |----------|--------------------------------------|
 * | `L1`     | `USER_ROLEPLAY_LEVEL_2_BEGINNER`     |
 * | `L2`     | `USER_ROLEPLAY_LEVEL_3_INTERMEDIATE` |
 * | `L3`     | `USER_ROLEPLAY_LEVEL_4_ADVANCED`     |
 * | `L4`     | `USER_ROLEPLAY_LEVEL_5_EXPERT`       |
 *
 * The incoming skill code is first normalized to a stable level ID via
 * `SKILL_LEVEL_BY_CODE`, which means both canonical and legacy aliases are
 * handled by shared scoring constants.
 *
 * @param score - The simulation score object, or `null` when no score exists yet.
 * @returns A non-empty description string for known skill levels; falls back to
 *   `score.description` for unrecognised levels, or `''` when either the score
 *   or its description is absent.
 */
export function getScoreCardDescription(score: ISimulationScore | null): string {
  // Guard: return early with an empty string when there is no score to evaluate.
  // This covers the pre-submission state where a score object has not yet been
  // created for the user's session.
  if (!score) return '';

  // Derive the user's skill tier from their raw numeric score.
  // `getSkillLevel` handles score conversion (e.g. percentage → 1–4 scale) and
  // returns one of the SKILL_LEVELS constants, or null for unrecognised inputs.
  // Pass runtime rating cutoffs if available to use backend-supplied thresholds.
  const level = getSkillLevel(score.score, score.runtimeRatingCutoffs);

  // Resolve to stable level IDs (L1-L4) so this logic stays resilient if
  // canonical or legacy skill code strings evolve in one central place.
  const skillLevelId = level ? SKILL_LEVEL_BY_CODE[level] : null;

  // If score policy returns unknown/null, fall back to backend description.
  if (!skillLevelId || !SKILL_LEVEL_DEFINITIONS[skillLevelId]) {
    return score.description || '';
  }

  // Map stable level IDs to the appropriate coaching descriptions.
  switch (skillLevelId) {
    case 'L1':
      return SCORE_LABELS.USER_ROLEPLAY_LEVEL_2_BEGINNER;
    case 'L2':
      return SCORE_LABELS.USER_ROLEPLAY_LEVEL_3_INTERMEDIATE;
    case 'L3':
      return SCORE_LABELS.USER_ROLEPLAY_LEVEL_4_ADVANCED;
    case 'L4':
      return SCORE_LABELS.USER_ROLEPLAY_LEVEL_5_EXPERT;
    default:
      // Defensive fallback for any future ID added without description mapping.
      return score.description || '';
  }
}
